// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the drawing kit every in-headset panel is built from (the dock,
 * the side panels, the legend, the readout). A Quest browser gives an AR
 * session no DOM overlay, so everything a person reads or presses in the
 * headset is a canvas on a plane; this module is what keeps those planes
 * looking and behaving alike.
 *
 * Three parts:
 *   - `PanelPainter`: canvas drawing helpers (rounded boxes, text that
 *     fits, buttons, chips, switches, icons) that record a hit rectangle
 *     for everything pressable as they draw it, so the picture and the
 *     hit test can never disagree about where a button is.
 *   - `hitAt` / `uvToCanvas`: the pure hit test, in canvas pixels, from a
 *     raycast's UV (THREE's plane UV grows upward, a canvas's y does not).
 *   - `createCanvasPanel`: the plane, its texture and its material, with
 *     mipmaps on — the panels of the first VR version sampled a 2:1-down
 *     texture without them, which is why their small text shimmered.
 *
 * Sizes are chosen so 1 canvas pixel is about 0.5 mm at the panel, and no
 * text is drawn under 22 px (about 1.1 cm, readable at arm's length).
 */

import type * as THREE from 'three'

/** The palette every panel draws in — the 2D tokens (src/styles/tokens.css). */
export const PANEL_COLORS = {
  bg: 'rgba(13, 13, 18, 0.9)',
  border: 'rgba(255, 255, 255, 0.14)',
  surface: 'rgba(255, 255, 255, 0.07)',
  surfaceHover: 'rgba(255, 255, 255, 0.12)',
  text: '#e8eaf0',
  textSecondary: '#bbbbbb',
  muted: '#9a9aa6',
  accent: '#22c55e',
  accentSoft: 'rgba(34, 197, 94, 0.2)',
  accentText: '#bbf7d0',
  accentDark: '#15803d',
  danger: 'rgba(239, 68, 68, 0.16)',
  dangerText: '#fecaca',
  warning: '#ffcc66',
} as const

const SANS = 'system-ui, -apple-system, "Segoe UI", sans-serif'
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

/** A pressable rectangle in canvas pixels, and what pressing it does. */
export interface PanelHit<A> {
  x: number
  y: number
  w: number
  h: number
  action: A
}

/** The last hit drawn wins, as the last thing drawn is on top. */
export function hitAt<A>(hits: readonly PanelHit<A>[], x: number, y: number): A | null {
  for (let i = hits.length - 1; i >= 0; i--) {
    const h = hits[i]
    if (x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h) return h.action
  }
  return null
}

/** A plane's UV (v grows upward) as canvas pixels (y grows downward). */
export function uvToCanvas(uv: { x: number; y: number }, w: number, h: number): { x: number; y: number } {
  return { x: uv.x * w, y: (1 - uv.y) * h }
}

/**
 * A scrolling list on a panel: its viewport in canvas pixels and the
 * height of everything in it. The scroll bar takes the viewport's right
 * edge, SCROLLBAR_W wide (owner, 2026-10-04: lists scroll, with a bar,
 * rather than turning pages).
 */
export interface ScrollRegion {
  x: number
  y: number
  w: number
  h: number
  content: number
}

export const SCROLLBAR_W = 22

/** The furthest a list can scroll. */
export function maxScroll(region: ScrollRegion): number {
  return Math.max(0, region.content - region.h)
}

export function clampScroll(scroll: number, region: ScrollRegion): number {
  return Math.max(0, Math.min(maxScroll(region), scroll))
}

/** The thumb's top and height in canvas pixels; null when everything fits. */
export function scrollbarThumb(region: ScrollRegion, scroll: number): { y: number; h: number } | null {
  if (region.content <= region.h) return null
  const h = Math.max(60, (region.h * region.h) / region.content)
  const travel = region.h - h
  const t = clampScroll(scroll, region) / maxScroll(region)
  return { y: region.y + t * travel, h }
}

/** The scroll that puts the thumb's top at `thumbTop` (dragging the thumb). */
export function scrollForThumb(region: ScrollRegion, thumbTop: number): number {
  const thumb = scrollbarThumb(region, 0)
  if (!thumb) return 0
  const travel = region.h - thumb.h
  if (travel <= 0) return 0
  return clampScroll(((thumbTop - region.y) / travel) * maxScroll(region), region)
}

/** Whether a canvas point is on a region's scroll bar (with a little slack for a wobbling ray). */
export function onScrollbar(region: ScrollRegion, x: number, y: number): boolean {
  return x >= region.x + region.w - SCROLLBAR_W - 14 && x <= region.x + region.w + 8 && y >= region.y && y <= region.y + region.h
}

export function insideRegion(region: ScrollRegion, x: number, y: number): boolean {
  return x >= region.x && x <= region.x + region.w && y >= region.y && y <= region.y + region.h
}

export type PanelIcon =
  | 'play' | 'pause' | 'step-back' | 'step-forward' | 'rewind' | 'sound' | 'muted'
  | 'browse' | 'layers' | 'pin' | 'view' | 'help' | 'exit' | 'close' | 'search'
  | 'lock' | 'backspace' | 'chevron-left' | 'chevron-right' | 'check' | 'eye' | 'eye-off' | 'pencil'
  | 'grip' | 'minus' | 'plus' | 'spin' | 'mic'

export interface TextOptions {
  size?: number
  weight?: number
  color?: string
  align?: CanvasTextAlign
  baseline?: CanvasTextBaseline
  maxWidth?: number
  mono?: boolean
}

export interface ButtonOptions {
  active?: boolean
  disabled?: boolean
  /** Filled in the dark accent, white text: the one primary action. */
  primary?: boolean
  danger?: boolean
  icon?: PanelIcon
  size?: number
  radius?: number
  /** Icon above the label (dock style) instead of beside it. */
  stacked?: boolean
}

/**
 * Canvas drawing with hit rectangles recorded as it goes. One painter per
 * redraw: `hits` is what that redraw made pressable.
 */
export class PanelPainter<A> {
  readonly hits: PanelHit<A>[] = []

  constructor(
    readonly g: CanvasRenderingContext2D,
    readonly w: number,
    readonly h: number,
  ) {}

  hit(x: number, y: number, w: number, h: number, action: A): void {
    this.hits.push({ x, y, w, h, action })
  }

  /** Clear, then the panel's glass and border. */
  frame(radius = 28): void {
    const { g } = this
    g.clearRect(0, 0, this.w, this.h)
    g.fillStyle = PANEL_COLORS.bg
    this.roundPath(1, 1, this.w - 2, this.h - 2, radius)
    g.fill()
    g.strokeStyle = PANEL_COLORS.border
    g.lineWidth = 2
    g.stroke()
  }

  roundPath(x: number, y: number, w: number, h: number, r: number): void {
    const { g } = this
    g.beginPath()
    if (typeof g.roundRect === 'function') {
      g.roundRect(x, y, w, h, Math.min(r, w / 2, h / 2))
    } else {
      g.rect(x, y, w, h)
    }
  }

  box(x: number, y: number, w: number, h: number, r: number, fill: string, stroke?: string, lineWidth = 2): void {
    this.roundPath(x, y, w, h, r)
    this.g.fillStyle = fill
    this.g.fill()
    if (stroke) {
      this.g.strokeStyle = stroke
      this.g.lineWidth = lineWidth
      this.g.stroke()
    }
  }

  font(size: number, weight = 500, mono = false): string {
    return `${weight} ${size}px ${mono ? MONO : SANS}`
  }

  /** Shorten `text` with an ellipsis until it fits `maxWidth` at the current font. */
  fit(text: string, maxWidth: number): string {
    const { g } = this
    if (g.measureText(text).width <= maxWidth) return text
    let s = text
    while (s.length > 1 && g.measureText(`${s}…`).width > maxWidth) s = s.slice(0, -1)
    return `${s.trimEnd()}…`
  }

  measure(text: string, size: number, weight = 500, mono = false): number {
    this.g.font = this.font(size, weight, mono)
    return this.g.measureText(text).width
  }

  text(text: string, x: number, y: number, opts: TextOptions = {}): void {
    const { g } = this
    g.font = this.font(opts.size ?? 26, opts.weight ?? 500, opts.mono)
    g.fillStyle = opts.color ?? PANEL_COLORS.text
    g.textAlign = opts.align ?? 'left'
    g.textBaseline = opts.baseline ?? 'middle'
    g.fillText(opts.maxWidth ? this.fit(text, opts.maxWidth) : text, x, y)
  }

  /** Words broken onto at most `maxLines` lines that fit; the last ends in an ellipsis when cut. */
  wrap(text: string, maxWidth: number, maxLines: number, size: number, weight = 500): string[] {
    const { g } = this
    g.font = this.font(size, weight)
    const words = text.split(/\s+/).filter(Boolean)
    const lines: string[] = []
    let line = ''
    for (let i = 0; i < words.length; i++) {
      const next = line ? `${line} ${words[i]}` : words[i]!
      if (g.measureText(next).width <= maxWidth || !line) {
        line = next
        continue
      }
      lines.push(line)
      line = words[i]!
      if (lines.length === maxLines - 1) {
        line = words.slice(i).join(' ')
        break
      }
    }
    if (line) lines.push(line)
    const last = lines.length - 1
    if (last >= 0) lines[last] = this.fit(lines[last]!, maxWidth)
    return lines
  }

  /** A pressable button. Records its hit unless disabled. */
  button(x: number, y: number, w: number, h: number, label: string, action: A, opts: ButtonOptions = {}): void {
    const r = opts.radius ?? 16
    const fill = opts.primary
      ? PANEL_COLORS.accentDark
      : opts.active
        ? PANEL_COLORS.accentSoft
        : opts.danger
          ? PANEL_COLORS.danger
          : PANEL_COLORS.surface
    this.box(x, y, w, h, r, fill, opts.active ? PANEL_COLORS.accent : undefined, 3)
    const color = opts.disabled
      ? 'rgba(232, 234, 240, 0.35)'
      : opts.primary
        ? '#ffffff'
        : opts.active
          ? PANEL_COLORS.accentText
          : opts.danger
            ? PANEL_COLORS.dangerText
            : PANEL_COLORS.text
    const size = opts.size ?? 24
    if (opts.icon && opts.stacked) {
      const iconSize = Math.min(h * 0.38, 40)
      this.icon(opts.icon, x + w / 2, y + h * 0.4, iconSize, color)
      if (label) this.text(label, x + w / 2, y + h * 0.78, { size, weight: 600, color, align: 'center', maxWidth: w - 12 })
    } else if (opts.icon && label) {
      const iconSize = Math.min(h * 0.5, 34)
      this.g.font = this.font(size, 600)
      const tw = Math.min(this.g.measureText(label).width, w - iconSize - 40)
      const total = iconSize + 12 + tw
      const left = x + (w - total) / 2
      this.icon(opts.icon, left + iconSize / 2, y + h / 2, iconSize, color)
      this.text(label, left + iconSize + 12, y + h / 2, { size, weight: 600, color, maxWidth: tw + 1 })
    } else if (opts.icon) {
      this.icon(opts.icon, x + w / 2, y + h / 2, Math.min(h * 0.5, 40), color)
    } else {
      this.text(label, x + w / 2, y + h / 2, { size, weight: 600, color, align: 'center', maxWidth: w - 16 })
    }
    if (!opts.disabled) this.hit(x, y, w, h, action)
  }

  /** A pill chip; returns its width so a row can be laid out. */
  chip(x: number, y: number, label: string, action: A, active: boolean, size = 24, h = 52, pad = 20): number {
    const w = this.measure(label, size, 600) + pad * 2
    this.box(x, y, w, h, h / 2, active ? PANEL_COLORS.accentSoft : PANEL_COLORS.surface, active ? PANEL_COLORS.accent : PANEL_COLORS.border, active ? 3 : 1.5)
    this.text(label, x + w / 2, y + h / 2 + 1, { size, weight: 600, align: 'center', color: active ? PANEL_COLORS.accentText : PANEL_COLORS.text })
    this.hit(x, y, w, h, action)
    return w
  }

  /** An on/off switch. */
  toggle(x: number, y: number, on: boolean, action: A, disabled = false): void {
    const w = 84
    const h = 46
    this.box(x, y, w, h, h / 2, on ? PANEL_COLORS.accentDark : 'rgba(255, 255, 255, 0.2)')
    this.g.beginPath()
    this.g.arc(on ? x + w - h / 2 : x + h / 2, y + h / 2, h / 2 - 6, 0, Math.PI * 2)
    this.g.fillStyle = disabled ? 'rgba(255,255,255,0.4)' : '#ffffff'
    this.g.fill()
    if (!disabled) this.hit(x - 8, y - 8, w + 16, h + 16, action)
  }

  /**
   * AYNI (owner, 2026-10-20): a checkbox with its label after it, `cy` its
   * middle; the box and the label are one target. Returns its width.
   */
  checkbox(x: number, cy: number, on: boolean, label: string, action: A, size = 24, color: string = PANEL_COLORS.accent): number {
    const box = 36
    const gap = 14
    const w = box + gap + this.measure(label, size, 600)
    this.box(x, cy - box / 2, box, box, 8, on ? color : 'rgba(255, 255, 255, 0.08)', on ? color : 'rgba(255, 255, 255, 0.45)', 2)
    if (on) this.icon('check', x + box / 2, cy, 26, '#111111')
    this.text(label, x + box + gap, cy, { size, weight: 600, color: on ? PANEL_COLORS.text : PANEL_COLORS.textSecondary })
    this.hit(x - 10, cy - 26, w + 20, 52, action)
    return w
  }

  /** Clip what is drawn next to a rectangle; `unclip` ends it. */
  clip(x: number, y: number, w: number, h: number): void {
    this.g.save()
    this.g.beginPath()
    this.g.rect(x, y, w, h)
    this.g.clip()
  }

  unclip(): void {
    this.g.restore()
  }

  /** A region's scroll bar: a track, and a thumb when the list is longer than its viewport. */
  scrollbar(region: ScrollRegion, scroll: number): void {
    const thumb = scrollbarThumb(region, scroll)
    if (!thumb) return
    const x = region.x + region.w - SCROLLBAR_W
    this.box(x, region.y, SCROLLBAR_W, region.h, SCROLLBAR_W / 2, 'rgba(255, 255, 255, 0.08)')
    this.box(x + 3, thumb.y + 3, SCROLLBAR_W - 6, thumb.h - 6, (SCROLLBAR_W - 6) / 2, 'rgba(232, 234, 240, 0.7)')
  }

  /** A small stroked icon centred on (cx, cy). */
  icon(name: PanelIcon, cx: number, cy: number, size: number, color: string): void {
    const { g } = this
    const s = size / 24
    g.save()
    g.translate(cx - 12 * s, cy - 12 * s)
    g.scale(s, s)
    g.strokeStyle = color
    g.fillStyle = color
    g.lineWidth = 2.2
    g.lineCap = 'round'
    g.lineJoin = 'round'
    const line = (...pts: number[]): void => {
      g.beginPath()
      g.moveTo(pts[0]!, pts[1]!)
      for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i]!, pts[i + 1]!)
      g.stroke()
    }
    const poly = (fill: boolean, ...pts: number[]): void => {
      g.beginPath()
      g.moveTo(pts[0]!, pts[1]!)
      for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i]!, pts[i + 1]!)
      g.closePath()
      if (fill) g.fill()
      else g.stroke()
    }
    switch (name) {
      case 'play': poly(true, 7, 4.5, 7, 19.5, 19.5, 12); break
      case 'pause': g.lineWidth = 3.4; line(8.5, 5, 8.5, 19); line(15.5, 5, 15.5, 19); break
      case 'step-back': poly(false, 18, 6, 10, 12, 18, 18); line(6, 6, 6, 18); break
      case 'step-forward': poly(false, 6, 6, 14, 12, 6, 18); line(18, 6, 18, 18); break
      case 'rewind': poly(false, 11, 7, 5, 12, 11, 17); poly(false, 19, 7, 13, 12, 19, 17); break
      case 'sound':
        poly(false, 4, 10, 8, 10, 13, 6, 13, 18, 8, 14, 4, 14)
        g.beginPath(); g.arc(14, 12, 4, -Math.PI / 3, Math.PI / 3); g.stroke()
        g.beginPath(); g.arc(14, 12, 7.5, -Math.PI / 3, Math.PI / 3); g.stroke()
        break
      // AYNI (Quest review B3): the voice button's own icon (it used the speaker beside Mute).
      case 'mic':
        g.beginPath()
        if (typeof g.roundRect === 'function') g.roundRect(9, 3, 6, 11, 3)
        else g.rect(9, 3, 6, 11)
        g.stroke()
        g.beginPath(); g.arc(12, 11, 6, 0, Math.PI); g.stroke()
        line(12, 17, 12, 21); line(8.5, 21, 15.5, 21)
        break
      case 'muted':
        poly(false, 4, 10, 8, 10, 13, 6, 13, 18, 8, 14, 4, 14)
        line(16, 9, 21, 15); line(21, 9, 16, 15)
        break
      case 'browse':
        for (const [x, y] of [[4, 4], [13, 4], [4, 13], [13, 13]] as const) {
          g.beginPath()
          if (typeof g.roundRect === 'function') g.roundRect(x, y, 7, 7, 1.5)
          else g.rect(x, y, 7, 7)
          g.stroke()
        }
        break
      case 'layers': poly(false, 12, 3, 21, 8, 12, 13, 3, 8); line(3, 13, 12, 18, 21, 13); break
      case 'pin':
        g.beginPath()
        g.moveTo(12, 21)
        g.bezierCurveTo(12, 21, 6, 15.5, 6, 10)
        g.arc(12, 10, 6, Math.PI, 0)
        g.bezierCurveTo(18, 15.5, 12, 21, 12, 21)
        g.stroke()
        g.beginPath(); g.arc(12, 10, 2.2, 0, Math.PI * 2); g.stroke()
        break
      case 'view':
        line(4, 7, 14, 7); line(18, 7, 20, 7); line(4, 17, 8, 17); line(12, 17, 20, 17)
        g.beginPath(); g.arc(16, 7, 2, 0, Math.PI * 2); g.stroke()
        g.beginPath(); g.arc(10, 17, 2, 0, Math.PI * 2); g.stroke()
        break
      case 'help':
        g.beginPath(); g.arc(12, 12, 9, 0, Math.PI * 2); g.stroke()
        g.beginPath(); g.arc(12, 10, 2.6, Math.PI * 1.05, Math.PI * 0.35); g.stroke()
        line(12, 13.2, 12, 14.4)
        g.beginPath(); g.arc(12, 17.4, 0.9, 0, Math.PI * 2); g.fill()
        break
      case 'exit':
        line(14, 4, 18, 4, 18, 20, 14, 20)
        line(10, 8, 6, 12, 10, 16); line(6, 12, 16, 12)
        break
      case 'close': line(6, 6, 18, 18); line(18, 6, 6, 18); break
      case 'search': g.beginPath(); g.arc(11, 11, 6.5, 0, Math.PI * 2); g.stroke(); line(16, 16, 20.5, 20.5); break
      case 'lock':
        g.beginPath()
        if (typeof g.roundRect === 'function') g.roundRect(5, 11, 14, 9, 2)
        else g.rect(5, 11, 14, 9)
        g.stroke()
        g.beginPath(); g.arc(12, 8, 4, Math.PI, 0); g.stroke(); line(8, 8, 8, 11); line(16, 8, 16, 11)
        break
      case 'backspace': poly(false, 9, 6, 21, 6, 21, 18, 9, 18, 3, 12); line(12, 9.5, 17, 14.5); line(17, 9.5, 12, 14.5); break
      case 'chevron-left': line(15, 5, 8, 12, 15, 19); break
      // AYNI: a real-time overlay's grip (two columns of dots), and a limit's steps.
      case 'grip':
        for (const [x, y] of [[9, 5], [15, 5], [9, 12], [15, 12], [9, 19], [15, 19]] as const) {
          g.beginPath(); g.arc(x, y, 1.9, 0, Math.PI * 2); g.fill()
        }
        break
      case 'minus': line(5, 12, 19, 12); break
      case 'plus': line(5, 12, 19, 12); line(12, 5, 12, 19); break
      // AYNI (2026-10-22): the dock's Spin — an open circle with an arrowhead, as the browser's ⟳.
      case 'spin':
        g.beginPath(); g.arc(12, 12, 8, -Math.PI * 0.35, Math.PI * 1.35); g.stroke()
        line(16.5, 2.5, 18.5, 6.5, 14.2, 7.5)
        break
      case 'chevron-right': line(9, 5, 16, 12, 9, 19); break
      case 'check': line(5, 12.5, 10, 17.5, 19, 7); break
      case 'pencil': poly(false, 4, 20, 5, 15.5, 16, 4.5, 19.5, 8, 8.5, 19); line(13.5, 7, 17, 10.5); break
      case 'eye':
      case 'eye-off':
        g.beginPath()
        g.moveTo(2, 12)
        g.quadraticCurveTo(12, 1, 22, 12)
        g.quadraticCurveTo(12, 23, 2, 12)
        g.stroke()
        g.beginPath(); g.arc(12, 12, 3, 0, Math.PI * 2); g.stroke()
        if (name === 'eye-off') line(3, 3, 21, 21)
        break
    }
    g.restore()
  }
}

/**
 * AYNI (owner, 2026-11-04): put `panel` in `dock`'s plane, `gap` metres
 * under the dock's bottom edge: the dock's turn, moved down along the
 * dock's own up. Turned to the eyes each from its own height, the two
 * planes leaned apart and the lower one looked tucked behind. Both planes
 * are centred on their position and unscaled; `scratch` is overwritten.
 */
export function placeUnderDock(
  panel: THREE.Object3D,
  dock: THREE.Object3D,
  dockHeight: number,
  panelHeight: number,
  gap: number,
  scratch: THREE.Vector3,
): void {
  scratch.set(0, 1, 0).applyQuaternion(dock.quaternion)
  panel.quaternion.copy(dock.quaternion)
  panel.position.copy(dock.position).addScaledVector(scratch, -(dockHeight / 2 + gap + panelHeight / 2))
}

export interface CanvasPanelOptions {
  /** Plane size in metres. */
  widthM: number
  heightM: number
  /** Canvas size in pixels; keep its aspect the plane's. */
  canvasW: number
  canvasH: number
  /** Draw order; UI draws over the globe (`depthTest` off). */
  renderOrder?: number
}

export interface CanvasPanel<A> {
  readonly mesh: THREE.Mesh
  /** Redraw: `paint` draws on a fresh painter whose hits become the panel's. */
  draw(paint: (p: PanelPainter<A>) => void): void
  /** The action under a raycast UV, or null. */
  hitTest(uv: { x: number; y: number }): A | null
  /** Canvas pixels for a raycast UV (for drag scrolling). */
  toCanvas(uv: { x: number; y: number }): { x: number; y: number }
  dispose(): void
}

export function createCanvasPanel<A>(THREE_: typeof THREE, opts: CanvasPanelOptions): CanvasPanel<A> {
  const canvas = document.createElement('canvas')
  canvas.width = opts.canvasW
  canvas.height = opts.canvasH
  const g = canvas.getContext('2d')
  if (!g) throw new Error('[VR panel] 2D canvas context unavailable')
  const texture = new THREE_.CanvasTexture(canvas)
  texture.colorSpace = THREE_.SRGBColorSpace
  texture.generateMipmaps = true
  texture.minFilter = THREE_.LinearMipmapLinearFilter
  texture.magFilter = THREE_.LinearFilter
  texture.anisotropy = 4
  const material = new THREE_.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  })
  const geometry = new THREE_.PlaneGeometry(opts.widthM, opts.heightM)
  const mesh = new THREE_.Mesh(geometry, material)
  mesh.renderOrder = opts.renderOrder ?? 10
  let hits: PanelHit<A>[] = []

  return {
    mesh,
    draw(paint) {
      const painter = new PanelPainter<A>(g, opts.canvasW, opts.canvasH)
      paint(painter)
      hits = painter.hits
      texture.needsUpdate = true
    },
    hitTest(uv) {
      const p = uvToCanvas(uv, opts.canvasW, opts.canvasH)
      return hitAt(hits, p.x, p.y)
    },
    toCanvas(uv) {
      return uvToCanvas(uv, opts.canvasW, opts.canvasH)
    },
    dispose() {
      texture.dispose()
      material.dispose()
      geometry.dispose()
    },
  }
}
